/**
 * shared/lib/communication-reference/index.ts
 *
 * ONE definition of "what does a genuine Strenn message look like" — imported
 * by both the client (the /official page at client/src/pages/official-channels.tsx,
 * and the Security Centre section at client/src/pages/security-centre/communication-rules.tsx)
 * and the server (server/services/email/shared.ts's anti-phishing footer), so
 * the two surfaces can never drift into describing different channels.
 *
 * Security tier ladder, Stage 6A ("Unify authoritative communication rules"),
 * Task 1 — reference-copy slice only [AC-90, AC-91]. Moved out of
 * official-channels.tsx, which used to hard-code this content locally.
 *
 * VERSIONED: bump COMMUNICATION_REFERENCE_VERSION (semver) and set
 * COMMUNICATION_REFERENCE_REVIEWED_ON to the date of the edit whenever
 * OFFICIAL_CHANNELS, NEVER_DO or OFFICIAL_EMAIL_SENDERS changes, so every
 * reader of /official can see when this was last checked against the real
 * senders. tests/unit/communication-reference-digest-lock.test.ts pins a hash
 * of all three so an edit that forgets the bump fails loudly (final fix wave,
 * I5).
 *
 * OPERATIONALLY HONEST (addendum item 2): every channel named in
 * OFFICIAL_CHANNELS must be backed by a real sender, and every real sender
 * must be described here — enforced by
 * tests/unit/communication-reference-sender-inventory.test.ts, which fails if
 * a `send*`/`notify*` export that actually composes+sends an email appears
 * under server/services/email/**, server/logic/payment-rail/** or
 * server/lib/** (widened scan roots, final fix wave ruling C-6) that
 * EMAIL_SENDER_INVENTORY does not list, or if EMAIL_SENDER_INVENTORY lists one
 * that no longer exists. Do NOT invent a channel or purpose here that no
 * sender implements.
 *
 * HONEST LABELLING (CLAUDE.md Friction Rule clause 2): this reference states
 * possession / enrollment facts only — what Strenn does and never does, and how
 * to verify independently. It must NEVER claim a live human is present, that
 * deepfakes are resisted, or that a message's look alone proves its origin. A
 * channel match is a SIGNAL, never proof — see
 * CHANNEL_MATCH_IS_SIGNAL_NOT_PROOF below.
 */

import { PRODUCT_DOMAIN, PRODUCT_NAME } from "../../brand";

// ---------------------------------------------------------------------------
// Version
// ---------------------------------------------------------------------------

export const COMMUNICATION_REFERENCE_VERSION = "1.3.0";
export const COMMUNICATION_REFERENCE_REVIEWED_ON = "2026-09-24";

/**
 * Path of the versioned public reference page. Final fix wave (I7): the
 * client route registration itself (client/src/App.tsx's `<Route
 * path="/official">`) stays a wouter literal — wouter route paths are matched
 * at render, not imported as a value — but every LINK SITE that points a
 * reader at that route cites this constant instead of repeating the literal:
 * client/src/pages/security-centre/communication-rules.tsx (the Security
 * Centre card), client/src/pages/auth-entry/auth-entry-view.tsx (sign-in),
 * client/src/pages/recovery/index.tsx (recovery entry),
 * client/src/components/layout/sidebar.tsx (desktop shell footer) and
 * client/src/components/layout/more-sheet.tsx (mobile shell footer). The
 * server's anti-phishing footer literal (server/services/email/shared.ts)
 * cites it too — so every one of those six places can never point at a
 * different URL than the route actually registered.
 */
export const OFFICIAL_CHANNELS_PATH = "/official";

// ---------------------------------------------------------------------------
// Brand domain + official email sender allowlist (final fix wave, ruling C-5)
// ---------------------------------------------------------------------------

/**
 * The canonical brand domain. Every domain literal on the honesty-sensitive
 * surfaces this module feeds (OFFICIAL_CHANNELS' Website entry,
 * server/services/email/shared.ts's ANTI_PHISHING_FOOTER) derives from this
 * ONE constant rather than repeating the string.
 *
 * Re-exported from `shared/brand.ts` rather than redeclared: the product name
 * and its domain rename together, and two constants holding the same fact are
 * two constants that can disagree.
 */
export const BRAND_DOMAIN = PRODUCT_DOMAIN;

/**
 * Every sender address `EMAIL_FROM` is allowed to resolve to (ruling C-5).
 * Only the brand-domain address: Resend's shared `onboarding@resend.dev`
 * pre-launch address was pruned with the move to Amazon SES (#3339, ruling
 * C-10), which sends only from the verified `strenn.com.au` identity.
 * server/lib/env-validate.ts refuses to boot if `EMAIL_FROM` is set to
 * anything outside this list.
 */
export const OFFICIAL_EMAIL_SENDERS: readonly string[] = [
  `noreply@${BRAND_DOMAIN}`, // launch address
];

/**
 * Honest-labelling caveat (CLAUDE.md Friction Rule clause 2; final fix wave,
 * I2). A channel match is evidence the reader can check, never proof of
 * anything on its own — the Strenn app itself is the only place an action is
 * real. Rendered directly under the channels table on /official and never
 * paraphrased into a stronger claim.
 */
export const CHANNEL_MATCH_IS_SIGNAL_NOT_PROOF =
  "A matching sender address, logo or safety phrase is a signal, never proof — the app is the only place an action is real.";

// ---------------------------------------------------------------------------
// Content — official channel definitions
// ---------------------------------------------------------------------------

export interface OfficialChannelEntry {
  readonly label: string;
  readonly value: string;
}

export const OFFICIAL_CHANNELS: readonly OfficialChannelEntry[] = [
  {
    label: "Website",
    value: `${BRAND_DOMAIN} — and only ${BRAND_DOMAIN}. No other domain is ${PRODUCT_NAME}.`,
  },
  {
    label: "Email senders",
    value:
      `Official emails come from exactly one of these sender addresses: ${OFFICIAL_EMAIL_SENDERS.join(", ")}. ` +
      `Any other sender address is not us. The address in use is printed in the footer of every ${PRODUCT_NAME} email.`,
  },
  {
    label: "SMS senders",
    value:
      "Official texts carry a one-time verification code only, for you to type into the app yourself — " +
      "never a link, and never a request to reply.",
  },
];

export const NEVER_DO: readonly string[] = [
  "Never ask you to approve, paste, read out, or confirm anything outside the app.",
  "DM, call, or text you asking for codes or approvals.",
  "Ask for your safety phrase.",
  "Send a link that approves a recovery or signs in on your behalf.",
];

// ---------------------------------------------------------------------------
// Operational inventory — kept honest by
// tests/unit/communication-reference-sender-inventory.test.ts
// ---------------------------------------------------------------------------

export interface SenderInventoryEntry {
  /** Exported function name that composes + sends this template. */
  readonly exportName: string;
  /** Repo-root-relative path of the file that exports it. */
  readonly file: string;
}

/**
 * Every live `send*`/`notify*` export that actually composes and sends an
 * email — under server/services/email/**, server/logic/payment-rail/** or
 * server/lib/** (widened scan roots, final fix wave ruling C-6). Each one
 * routes through `composeEmail` (server/services/email/shared.ts), which
 * guarantees the ANTI_PHISHING_FOOTER on every send — so "Email senders"
 * above is never more permissive than what the code actually sends, and never
 * silently missing one.
 *
 * Deliberately excludes wrapper/fan-out functions that only loop over another
 * listed sender rather than composing their own template (e.g.
 * `notifyMemberLifecycleAdminsByEmail`, which calls `sendMemberLifecycleNotice`
 * per recipient) — those aren't a distinct template.
 *
 * `opsAlert` is the one exception to the `send*`/`notify*` naming convention
 * above: it emails `OPS_ALERT_EMAIL` (audience: operators, not end users) on
 * an uncaught 5xx, and is listed explicitly per ruling C-6 rather than
 * discovered by name pattern.
 */
export const EMAIL_SENDER_INVENTORY: readonly SenderInventoryEntry[] = [
  { exportName: "sendOtp", file: "server/services/email/otp.ts" },
  { exportName: "sendGuardianInvite", file: "server/services/email/alerts.ts" },
  { exportName: "sendOsintAlert", file: "server/services/email/alerts.ts" },
  { exportName: "sendNewSignInAlert", file: "server/services/email/alerts.ts" },
  { exportName: "sendAalDowngradeNotice", file: "server/services/email/alerts.ts" },
  { exportName: "sendMasterPasskeyNotice", file: "server/services/email/alerts.ts" },
  { exportName: "sendCounterpartyInvite", file: "server/services/email/alerts.ts" },
  { exportName: "sendChallengeIssuedEmail", file: "server/services/email/challenge-issued.ts" },
  { exportName: "sendGuardianDutyNoticeEmail", file: "server/services/email/guardian-duty-notice.ts" },
  { exportName: "sendMemberLifecycleNotice", file: "server/services/email/member-lifecycle-notice.ts" },
  { exportName: "sendPlatformInvite", file: "server/services/email/platform-invite-email.ts" },
  { exportName: "sendRecoveryNoticeEmail", file: "server/services/email/recovery-notice.ts" },
  { exportName: "sendSafetyPhraseChangeNotice", file: "server/services/email/safety-phrase-change-notice.ts" },
  // Widened scan roots (final fix wave, ruling C-6): server/logic/payment-rail/**.
  { exportName: "notifyIssuingOrg", file: "server/logic/payment-rail/fraud-notify.ts" },
  { exportName: "notifyOfficerKeyEnrolled", file: "server/logic/payment-rail/officer-lifecycle-notify.ts" },
  { exportName: "notifyOfficerKeyRevoked", file: "server/logic/payment-rail/officer-lifecycle-notify.ts" },
  // Widened scan roots (final fix wave, ruling C-6): server/lib/**. Audience is
  // operators, not end users — see the naming-convention exception noted above.
  { exportName: "opsAlert", file: "server/lib/ops-alert.ts" },
];

/**
 * server/services/sms.ts is transport-only (`smsProvider()` returns a
 * send(toE164, body) provider) — it carries no template of its own. The one
 * live SMS body is composed inline at its sole call site today. Listed by
 * FILE rather than export name so the sender-inventory test can assert no
 * OTHER file calls `smsProvider().send(...)` without this list changing too.
 */
export const SMS_SEND_CALL_SITES: readonly string[] = [
  "server/logic/payment-rail/invites.ts",
];
